import { c as _c } from "react/compiler-runtime";
import { getUploadImageKey } from '@onefootprint/core';
import { IcoEyeCrossed16 } from '@onefootprint/icons';
import type { DocumentFaceComparison } from '@onefootprint/request-types/dashboard';
import { LinkButton, LoadingSpinner, Tooltip, useObjectUrl } from '@onefootprint/ui';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import useCurrentEntity from '@/entities/components/details/hooks/use-current-entity';
import type useEntityVault from '@/entities/hooks/use-entity-vault';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import UploadImage from '../upload-image-item/components/upload-image';
type ReferenceImageProps = {
  comparison: DocumentFaceComparison;
  vault: ReturnType<typeof useEntityVault>;
};
const ReferenceImage = forwardRef<HTMLDivElement, ReferenceImageProps>((t0, ref) => {
  const $ = _c(16);
  const {
    comparison,
    vault
  } = t0;
  const {
    t
  } = useTranslation("entity-details");
  const {
    data: entity
  } = useCurrentEntity();
  const decryptControls = useDecryptControls();
  const field = getUploadImageKey({
    image: comparison.reference
  });
  const {
    objectUrl
  } = useObjectUrl(vault.data?.vault[field] as string | undefined ?? null);
  const handleDecrypt = async () => {
    if (!entity) {
      return;
    }
    const reason = t(_temp);
    const response = await decryptControls.decrypt({
      entity,
      fields: [field],
      reason
    });
    if (response) {
      vault.update({
        vault: response,
        transforms: {},
        dataKinds: {}
      });
    }
  };
  const t1 = "flex flex-col items-center w-full gap-2 scroll-mt-2";
  let t2;
  if ($[0] !== t) {
    t2 = t(_temp2);
    $[0] = t;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = <span className="text-label-2 text-primary">{t2}</span>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t(_temp3);
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex-shrink-0 text-tertiary"><IcoEyeCrossed16 /></div>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4) {
    t6 = <Tooltip content={t4} side="left">{t5}</Tooltip>;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t3 || $[10] !== t6) {
    t7 = <div className="flex items-center justify-between w-full gap-2 px-3 py-2 border border-solid rounded border-tertiary bg-primary">{t3}{t6}</div>;
    $[9] = t3;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = objectUrl ? <UploadImage objectUrl={objectUrl} alt={field} failureReasons={[]} /> : <div className="flex items-center justify-center w-full h-40 border border-dashed rounded border-tertiary">{decryptControls.isDecrypting([field]) ? <LoadingSpinner /> : <Tooltip disabled={comparison.canDecryptReference} content={t(_temp4)}><LinkButton disabled={!comparison.canDecryptReference} onClick={handleDecrypt}>{t(_temp5)}</LinkButton></Tooltip>}</div>;
  let t9;
  if ($[12] !== ref || $[13] !== t7 || $[14] !== t8) {
    t9 = <div className={t1} ref={ref}>{t7}{t8}</div>;
    $[12] = ref;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
});
export default ReferenceImage;
function _temp($) {
  return $.decrypt.documentDetailsReason;
}
function _temp2($_0) {
  return $_0.fieldset.documents.details.referenceImage.title;
}
function _temp3($_1) {
  return $_1.fieldset.documents.details.faceChecks.employeeOnly;
}
function _temp4($_2) {
  return $_2.fieldset.documents.details.decrypt.notAllowed;
}
function _temp5($_3) {
  return $_3.fieldset.documents.details.decrypt.decryptCta;
}